import React, { useState, useEffect, useRef } from 'react';
import { Box, Text, Button, Input, Textarea, VStack, HStack } from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useConfig } from '../contexts/ConfigContext';
import { useUser } from '../contexts/UserContext';
import { API_ROUTES, buildRoute, USER_TYPES } from '../constants';
import { sendLog, sendError, clearBusinessEventsCache, getCachedUserProfile, convertTimeToTimestamp, convertTimestampToTimeString } from '../utils';
import Calendar from '../components/Calendar';
import TimePicker from '../components/TimePicker';
import BackButton from '../components/BackButton';

const FormField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isTextarea?: boolean;
  showIcon?: boolean;
  iconType?: 'calendar' | 'dropdown';
  labelColor?: string;
  onClick?: () => void;
  readOnly?: boolean;
}> = ({ label, value, onChange, placeholder, isTextarea = false, showIcon = false, iconType, labelColor = "#90909A", onClick, readOnly = false }) => (
  <Box
    backgroundColor="#120F1B"
    borderRadius="28px"
    padding="22px 18px"
    width="332px"
    minHeight="113px"
  >
    <Text className="subtitle-text" >
      {label}
    </Text>

    <Box position="relative">
      {isTextarea ? (
        <Textarea
          className="textarea-business-event-creation"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          resize="none"
          minHeight="161px"
          padding="0"
        />
      ) : (
        <Input
          className='input-in-form'        
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          onClick={onClick}
          readOnly={readOnly}
          cursor={readOnly ? "pointer" : "text"}
        />
      )}

      {/* Icon */}
      {showIcon && (
        <Box
          position="absolute"
          right="0"
          top="50%"
          transform="translateY(-50%)"
          width="17px"
          height="17px"
        >
          {iconType === 'calendar' && (
            <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_1_4720)">
                <path d="M8.46879 16.9975C6.85668 16.9975 5.24509 16.998 3.63298 16.9975C1.97464 16.9964 0.656357 16.0506 0.162734 14.4684C0.0511502 14.1117 0.00970494 13.7199 0.0081109 13.344C-0.00411015 10.5162 0.000672001 7.68885 0.00226605 4.86154C0.0027974 3.38728 1.09206 1.94545 2.52352 1.58499C2.98048 1.46963 3.46613 1.45102 3.94063 1.42869C4.18718 1.41699 4.27485 1.34575 4.25253 1.10279C4.23818 0.94542 4.25253 0.783267 4.27007 0.625368C4.31045 0.269695 4.60747 0.000680403 4.94701 -0.000382894C5.28229 -0.000914543 5.57612 0.244176 5.62554 0.592937C5.65423 0.793369 5.65264 0.999117 5.65051 1.20221C5.64892 1.35798 5.70099 1.42231 5.86624 1.42178C7.61969 1.41752 9.37315 1.41752 11.1271 1.42178C11.295 1.42178 11.345 1.35266 11.3391 1.19955C11.3328 1.03155 11.3296 0.862483 11.3391 0.694482C11.362 0.303189 11.6372 0.0230096 12.0108 0.000148754C12.3482 -0.0205855 12.6574 0.247365 12.7132 0.625368C12.7377 0.79071 12.7488 0.961901 12.7377 1.12831C12.7228 1.34788 12.8025 1.41699 13.0235 1.4255C13.455 1.44145 13.896 1.4473 14.3136 1.54193C15.8508 1.89069 16.9651 3.28202 16.9799 4.93066C17.0055 7.81113 17.0055 10.6921 16.9799 13.5726C16.9629 15.4589 15.3418 16.9879 13.4103 16.9959C11.7632 17.0028 10.116 16.9975 8.46826 16.997L8.46879 16.9975ZM8.49111 7.06257C6.23287 7.06257 3.97464 7.06629 1.7164 7.05779C1.46879 7.05672 1.39546 7.13541 1.39759 7.37784C1.40769 8.44114 1.40025 9.50496 1.40237 10.5683C1.4045 11.5874 1.38006 12.6077 1.4215 13.6252C1.46667 14.7348 2.3944 15.5923 3.50014 15.5939C6.82108 15.5982 10.142 15.6019 13.4629 15.586C13.7786 15.5844 14.1224 15.502 14.4034 15.3584C15.2079 14.948 15.5931 14.2616 15.591 13.3514C15.5857 11.3663 15.5841 9.38109 15.5953 7.39591C15.5968 7.1168 15.498 7.05725 15.2398 7.05832C12.9906 7.06682 10.7414 7.0631 8.49164 7.0631L8.49111 7.06257ZM8.48898 5.67869C9.48101 5.67869 10.473 5.67869 11.4651 5.67869C12.7403 5.67869 14.0161 5.67337 15.2913 5.68347C15.5267 5.68507 15.6133 5.60957 15.5915 5.37831C15.574 5.19329 15.5862 5.00615 15.5713 4.82114C15.4752 3.64354 14.5639 2.8046 13.3827 2.8046C10.1144 2.8046 6.84658 2.80725 3.57825 2.803C2.46985 2.80141 1.46454 3.68766 1.40981 4.73023C1.39918 4.93332 1.41938 5.13853 1.39865 5.34003C1.37049 5.61011 1.47145 5.68613 1.74084 5.68507C3.99058 5.67497 6.24031 5.67922 8.48951 5.67922L8.48898 5.67869Z" fill="white"/>
                <path d="M4.97122 8.47656C5.33094 8.50846 5.70289 8.49304 6.0472 8.58236C6.62318 8.73175 6.98025 9.14857 7.02807 9.7387C7.07642 10.3368 7.07058 10.9439 7.02913 11.5431C6.98663 12.1534 6.47653 12.6628 5.8676 12.6989C5.2252 12.7367 4.57749 12.7244 3.93455 12.6893C3.46272 12.6633 2.94624 12.1901 2.8947 11.6548C2.82828 10.9721 2.81978 10.2714 2.89577 9.5909C2.96484 8.97153 3.56739 8.53504 4.19863 8.51006C4.45474 8.49996 4.71192 8.50846 4.96856 8.50846C4.96962 8.49783 4.97015 8.48773 4.97122 8.47709V8.47656ZM4.23849 11.339C4.69598 11.339 5.11734 11.3448 5.53817 11.331C5.58705 11.3294 5.67047 11.2225 5.6726 11.1619C5.68588 10.8094 5.68907 10.4554 5.671 10.1029C5.66728 10.0354 5.5557 9.92158 5.48928 9.91786C5.13753 9.89925 4.78312 9.89925 4.43137 9.91786C4.36388 9.92158 4.24964 10.0332 4.24699 10.0992C4.22945 10.4952 4.23849 10.8924 4.23849 11.3384V11.339Z" fill="white"/>
              </g>
              <defs>
                <clipPath id="clip0_1_4720">
                  <rect width="17" height="17" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          )}
          {iconType === 'dropdown' && (
            <svg width="14" height="12" viewBox="0 0 7 4" fill="none">
              <path d="M1 1L3.5 3L6 1" stroke="#FFFFFF" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </Box>
      )}
    </Box>
  </Box>
);

const LargeFormField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
}> = ({ label, value, onChange, placeholder, minHeight = "161px" }) => (
  <Box
    backgroundColor="#120F1B"
    borderRadius="28px"
    padding="18px"
    width="332px"
    minHeight={minHeight}
  >
    <Text
      className="subtitle-text"
      marginBottom="16px"
    >
      {label}
    </Text>

    <Box position="relative">
      <Textarea
        className="textarea-business-event-creation"
        style={{ border: '1px solid #595C5D' }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        borderRadius="10px"
        resize="none"
        _focus={{
          border: "1px solid #C6D959 !important",
          boxShadow: "none"
        }}
        minHeight={minHeight}
        padding="12px"
      />

      <Text
        className='static-text'
        position="absolute"
        bottom="-20px"
        left="0"
      >
        minimum 300 characters
      </Text>
    </Box>
  </Box>
);

const BusinessEventCreationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { config } = useConfig();
  const { user } = useUser();

  // Check if we're in edit mode
  const isEditMode = location.state?.editMode || false;
  const eventId = location.state?.eventId || null;

  // Form state
  const [eventName, setEventName] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [date, setDate] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [startTime, setStartTime] = useState('');
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [endTime, setEndTime] = useState('');
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);
  const [dressCode, setDressCode] = useState('');
  const [showDressCodeDropdown, setShowDressCodeDropdown] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [eventDetails, setEventDetails] = useState('');
  const [eventRules, setEventRules] = useState('');
  const [proposedOffer, setProposedOffer] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Load event data when in edit mode
  useEffect(() => {
    const loadEventData = async () => {
      if (!isEditMode || !eventId || !config?.apiUrl) {
        return;
      }
      try {
        const response = await axios.get(`${config.apiUrl}${buildRoute(API_ROUTES.EVENTS_BY_ID, { id: eventId })}`);
        const eventData = response.data;

        // Load event data into form fields
        setEventName(eventData.eventName || '');
        setEventLocation(eventData.data?.eventLocation || '');
        
        // Extract date from startTime timestamp
        if (eventData.startTime) {
          const parsedDate = new Date(eventData.startTime);
          
          if (parsedDate && !isNaN(parsedDate.getTime())) {
            setSelectedDate(parsedDate);
            // Format date as "25 Jul, 2025" to match the form format
            const formattedDate = parsedDate.toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            });
            setDate(formattedDate);
          }
        }

        // Convert timestamps to time strings
        const startTimeStr = eventData.startTime !== undefined && eventData.startTime !== null
          ? convertTimestampToTimeString(eventData.startTime)
          : '';
        const endTimeStr = eventData.endTime !== undefined && eventData.endTime !== null
          ? convertTimestampToTimeString(eventData.endTime)
          : '';
        
        setStartTime(startTimeStr);
        setEndTime(endTimeStr);

        setDressCode(eventData.data?.dressCode || '');
        setEventDetails(eventData.data?.eventDetails || '');
        setEventRules(eventData.data?.eventRules || '');
        setProposedOffer(eventData.data?.proposedOffer || '');
      } catch (error: any) {
        console.error('Error loading event data:', error);
        sendError(error instanceof Error ? error : new Error('Failed to load event data'), {
          context: 'BusinessEventCreationPage.loadEventData',
          eventId
        });
        alert('Failed to load event data. Please try again.');
      }
    };

    loadEventData();
  }, [isEditMode, eventId, config?.apiUrl]);

  // Check if all required fields are filled
  const isFormValid = 
    eventName.trim() !== '' &&
    eventLocation.trim() !== '' &&
    date !== '' &&
    startTime !== '' &&
    endTime !== '';

  const handleSave = async () => {
    // Validate required fields
    if (!eventName.trim()) {
      alert('Please enter an event name');
      return;
    }

    if (!eventLocation.trim()) {
      alert('Please enter a location');
      return;
    }

    if (!date) {
      alert('Please select a date');
      return;
    }

    if (!startTime) {
      alert('Please select a start time');
      return;
    }

    if (!endTime) {
      alert('Please select an end time');
      return;
    }

    if (!config?.apiUrl) {
      sendError(new Error('API URL not configured'), {
        context: 'BusinessEventCreationPage.handleSave'
      });
      alert('Configuration error. Please try again later.');
      return;
    }

    setIsSaving(true);

    try {
      // Fetch business profile to get photos, city, country, and category
      let businessPhotos: string[] = [];
      let businessCity: string | undefined;
      let businessCountry: string | undefined;
      let businessCategory: string | undefined;
      if (user?.id && user?.userType === USER_TYPES.BUSINESS) {
        try {
          const businessProfile = await getCachedUserProfile(config.apiUrl, user.id, USER_TYPES.BUSINESS);
          if (businessProfile.data?.photos && Array.isArray(businessProfile.data.photos)) {
            businessPhotos = businessProfile.data.photos;
          }
          businessCity = businessProfile.city;
          businessCountry = businessProfile.country;
          businessCategory = businessProfile.businessCategory;
        } catch (profileError: any) {
          console.error('Error fetching business profile:', profileError);
          // Continue without photos/location/category if profile fetch fails
        }
      }

      // Convert time strings to timestamps
      const startTimeTimestamp = convertTimeToTimestamp(date, startTime);
      const endTimeTimestamp = convertTimeToTimestamp(date, endTime);

      if (startTimeTimestamp === null || endTimeTimestamp === null) {
        alert('Invalid time format. Please select valid start and end times.');
        setIsSaving(false);
        return;
      }

      const eventData = {
        eventName: eventName.trim(),
        eventLocation: eventLocation.trim(),
        startTime: startTimeTimestamp,
        endTime: endTimeTimestamp,
        dressCode: dressCode || undefined,
        eventDetails: eventDetails.trim() || undefined,
        eventRules: eventRules.trim() || undefined,
        proposedOffer: proposedOffer.trim() || undefined,
        photos: businessPhotos.length > 0 ? businessPhotos : undefined,
        city: businessCity,
        country: businessCountry,
        category: businessCategory
      };

      sendLog('BusinessEventCreationSaveAttempt', {
        eventName: eventData.eventName,
        isEditMode,
        hasEventDetails: !!eventData.eventDetails,
        hasEventRules: !!eventData.eventRules,
        hasProposedOffer: !!eventData.proposedOffer
      });

      let response;
      if (isEditMode && eventId) {
        // Update existing event
        response = await axios.put(`${config.apiUrl}${buildRoute(API_ROUTES.EVENTS_BY_ID, { id: eventId })}`, eventData);
        
        if (response.status === 200) {
          sendLog('BusinessEventUpdated', {
            eventId: eventId,
            eventName: eventData.eventName
          });
        } else {
          throw new Error('Unexpected response status: ' + response.status);
        }
      } else {
        // Create new event
        response = await axios.post(`${config.apiUrl}${API_ROUTES.EVENTS}`, eventData);
        
        if (response.status === 201) {
          sendLog('BusinessEventCreated', {
            eventId: response.data.id,
            eventName: eventData.eventName
          });
        } else {
          throw new Error('Unexpected response status: ' + response.status);
        }
      }
      
      // Clear events cache so the updated/new event appears in the list
      if (user?.id) {
        clearBusinessEventsCache(user.id);
      }
      
      // Navigate back to previous page on success
      navigate(-1);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save event';
      
      const errorToLog = error instanceof Error 
        ? error 
        : new Error(errorMessage);
      
      sendError(errorToLog, {
        context: 'BusinessEventCreationPage.handleSave',
        eventName: eventName,
        errorMessage,
        status: error.response?.status,
        responseData: error.response?.data
      });
      
      alert(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    // Format date as "25 Jul, 2025"
    const formattedDate = date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    setDate(formattedDate);
    setShowCalendar(false);
  };

  const handleCalendarClose = () => {
    setShowCalendar(false);
  };

  const handleDateFieldClick = () => {
    setShowCalendar(true);
  };

  const dressCodeOptions = [
    'White Tie',
    'Business Formal',
    'Casual',
    'Cocktail Attire',
    'Black Tie',
    'Black Tie Optional',
    'Business Casual',
    'Smart Casual',
    'Creative / Fashion Forward',
    'Festive / Holiday Attire'
  ];

  const handleDressCodeSelect = (option: string) => {
    setDressCode(option);
    setShowDressCodeDropdown(false);
  };

  const handleDressCodeFieldClick = () => {
    setShowDressCodeDropdown(true);
  };

  const handleStartTimeSelect = (time: string) => {
    setStartTime(time);
    setShowStartTimePicker(false);
  };

  const handleStartTimeFieldClick = () => {
    setShowStartTimePicker(true);
  };

  const handleStartTimePickerClose = () => {
    setShowStartTimePicker(false);
  };

  const handleEndTimeSelect = (time: string) => {
    setEndTime(time);
    setShowEndTimePicker(false);
  };

  const handleEndTimeFieldClick = () => {
    setShowEndTimePicker(true);
  };

  const handleEndTimePickerClose = () => {
    setShowEndTimePicker(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleShowCancelDialog = () => {
    setShowCancelDialog(true);
  };

  const handleCancelEventClose = () => {
    setShowCancelDialog(false);
    setCancelReason('');
  };

  const handleSendCancelReason = () => {
    // TODO: Implement send cancellation reason logic
    console.log('Sending cancellation reason:', cancelReason);
    setShowCancelDialog(false);
    setCancelReason('');
    // Navigate back after sending
    navigate(-1);
  };

  const handleScrollToTop = () => {
    console.log('Scroll to top clicked'); // Debug log

    // Try multiple approaches
    if (scrollContainerRef.current) {
      console.log('Using ref to scroll'); // Debug log
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Also try window scroll as fallback
    console.log('Using window scroll as fallback'); // Debug log
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Try document element scroll
    console.log('Using document element scroll'); // Debug log
    document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
    document.body.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      const scrollTop = target.scrollTop;
      console.log('Scroll detected:', scrollTop); // Debug log
      // Always show the button, don't hide it when at top
      setShowScrollToTop(true);
    };

    const scrollContainer = scrollContainerRef.current;
    console.log('Scroll container ref:', scrollContainer); // Debug log
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    } else {
      console.log('Scroll container ref not found in useEffect'); // Debug log
    }
  }, []);

  return (
    <Box
      className="base-container-long"
      ref={scrollContainerRef}
      data-scroll-container="true"
      onClick={(e) => {
        // Close calendar if clicking outside of it
        if (showCalendar && !(e.target as HTMLElement).closest('[data-calendar="true"]')) {
          setShowCalendar(false);
        }
        // Close dress code dropdown if clicking outside of it
        if (showDressCodeDropdown && !(e.target as HTMLElement).closest('[data-dropdown="true"]')) {
          setShowDressCodeDropdown(false);
        }
        // Close time pickers if clicking outside of them
        if (showStartTimePicker && !(e.target as HTMLElement).closest('[data-timepicker="true"]')) {
          setShowStartTimePicker(false);
        }
        if (showEndTimePicker && !(e.target as HTMLElement).closest('[data-timepicker="true"]')) {
          setShowEndTimePicker(false);
        }
      }}
    >


      {/* Navigation Bar */}
      <Box
        className='edit-profiles-top'
         marginBottom="60px"
      >
        <BackButton onClick={handleBack} />

        <Text
          className='title-text'
          marginLeft="24px"
        >
          Event creation
        </Text>
      </Box>

      {/* Divider */}
      <Box className="navigation-bar-divider"/>

      {/* Form Fields */}
      <VStack spacing="16px" alignItems="center" width="100%" padding="0 22px">
        <FormField
          label="Event name"
          value={eventName}
          onChange={setEventName}
          placeholder="Enter event name"
        />

        <FormField
          label="Location"
          value={eventLocation}
          onChange={setEventLocation}
          placeholder="Enter location"
        />

        <Box position="relative">
          <FormField
            label="Date"
            value={date}
            onChange={setDate}
            placeholder="Select date"
            showIcon={true}
            iconType="calendar"
            onClick={handleDateFieldClick}
            readOnly={true}
          />

          {showCalendar && (
            <Calendar
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              onClose={handleCalendarClose}
              validationType="future"
            />
          )}
        </Box>

        <Box position="relative">
          <FormField
            label="Start time"
            value={startTime}
            onChange={setStartTime}
            placeholder="Select start time"
            onClick={handleStartTimeFieldClick}
            readOnly={true}
          />

          {showStartTimePicker && (
            <TimePicker
              selectedTime={startTime}
              onTimeSelect={handleStartTimeSelect}
              onClose={handleStartTimePickerClose}
              type="start"
            />
          )}
        </Box>

        <Box position="relative">
          <FormField
            label="End time"
            value={endTime}
            onChange={setEndTime}
            placeholder="Select end time"
            onClick={handleEndTimeFieldClick}
            readOnly={true}
          />

          {showEndTimePicker && (
            <TimePicker
              selectedTime={endTime}
              onTimeSelect={handleEndTimeSelect}
              onClose={handleEndTimePickerClose}
              type="end"
              startTime={startTime}
            />
          )}
        </Box>

        <Box position="relative">
          <FormField
            label="Dress code"
            value={dressCode}
            onChange={setDressCode}
            placeholder="Select dress code"
            showIcon={true}
            iconType="dropdown"
            labelColor="#FFFFFF"
            onClick={handleDressCodeFieldClick}
            readOnly={true}
          />

          {showDressCodeDropdown && (
            <Box              
              position="absolute"
              top="100%"
              left="0"
              right="0"
              backgroundColor="#120F1B"
              borderRadius="10px"
              border="1px solid #595C5D"
              zIndex={1000}
              marginTop="8px"
              data-dropdown="true"
            >
              {dressCodeOptions.map((option, index) => (
                <Box
                  key={option}
                  padding="10px 14px"
                  cursor="pointer"
                  backgroundColor={dressCode === option ? "#020105" : "transparent"}
                  borderRadius={index === 0 ? "10px 10px 0 0" : index === dressCodeOptions.length - 1 ? "0 0 10px 10px" : "0"}
                  onClick={() => handleDressCodeSelect(option)}
                  _hover={{
                    backgroundColor: dressCode === option ? "#020105" : "rgba(255, 255, 255, 0.05)"
                  }}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Text
                    className='static-text'
                    fontWeight={dressCode === option ? "500" : "400"}
                  >
                    {option}
                  </Text>
                  {dressCode === option && (
                    <Box width="20px" height="20px" display="flex" alignItems="center" justifyContent="center">
                      <svg width="13.33" height="9.17" viewBox="0 0 13.33 9.17" fill="none">
                        <path d="M1 1L6.665 7.17L12.33 1" stroke="#C6D959" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <LargeFormField
          label="Event details"
          value={eventDetails}
          onChange={setEventDetails}
          placeholder="Describe your event..."
          minHeight="161px"
        />

        <LargeFormField
          label="Event rules"
          value={eventRules}
          onChange={setEventRules}
          placeholder="Enter event rules..."
          minHeight="472px"
        />

        <Box marginBottom="50px">
          <LargeFormField
            label="Proposed offer for members"
            value={proposedOffer}
            onChange={setProposedOffer}
            placeholder="Describe what you offer to influencers..."
            minHeight="268px"
          />
        </Box>
      </VStack>
      {/* Delete Event Button - Only in edit mode */}
      {isEditMode && (
        <Box            
          width="100%"
          padding="0 22px"
          marginBottom="60px"
          marginLeft="22px"
        >
          <HStack
            alignItems="center"
            cursor="pointer"
            spacing="8px"
            onClick={handleShowCancelDialog}
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.0241 20.961C13.2818 20.961 12.5353 21.0078 11.7983 20.9509C10.3516 20.8394 9.19389 19.7996 9.02235 18.4856C8.88722 17.4518 8.80048 16.4129 8.68979 15.3765C8.58102 14.362 8.46697 13.348 8.35915 12.3331C8.30357 11.8109 8.25086 11.2884 8.20965 10.7654C8.19335 10.5608 8.1186 10.4589 7.87374 10.4842C7.75011 10.4974 7.61929 10.4716 7.49374 10.4528C7.19377 10.4095 7.01216 10.2201 7.00066 9.95032C6.9882 9.65035 7.15208 9.43084 7.45253 9.36568C7.56754 9.34076 7.68973 9.33507 7.80857 9.33464C8.54316 9.33201 9.27823 9.33157 10.0128 9.33464C10.1561 9.33551 10.2395 9.31059 10.286 9.16147C10.6808 7.89599 11.8174 7.05816 13.2564 7.014C13.9393 6.99301 14.6408 6.97989 15.3064 7.0949C16.5293 7.30566 17.3118 8.02805 17.6817 9.10944C17.7406 9.28129 17.8188 9.33945 18.0123 9.33726C18.7627 9.32808 19.5136 9.3307 20.2645 9.33464C20.7638 9.33726 21.0279 9.57426 20.9977 9.97831C20.9804 10.2101 20.7993 10.4073 20.5444 10.4471C20.3723 10.4737 20.1955 10.486 20.0211 10.4864C19.874 10.4873 19.8021 10.5319 19.7887 10.6714C19.7154 11.4331 19.6382 12.1949 19.5592 12.9562C19.4528 13.9781 19.3421 15 19.2352 16.0219C19.1509 16.8265 19.0886 17.6337 18.9827 18.4357C18.8059 19.772 17.6707 20.8193 16.2034 20.9689C15.6116 21.0292 15.0069 20.9829 14.4079 20.985C14.28 20.985 14.1525 20.985 14.0246 20.985C14.0246 20.9767 14.0246 20.9689 14.0246 20.9606L14.0241 20.961ZM13.9771 19.8153C13.9771 19.821 13.9771 19.8267 13.9771 19.8324C14.5521 19.8324 15.1281 19.8451 15.7022 19.8271C15.9849 19.8184 16.2863 19.7996 16.5436 19.7051C17.3884 19.3942 17.7196 18.758 17.789 17.9656C17.8815 16.9131 17.9865 15.861 18.0962 14.8098C18.2385 13.4468 18.3895 12.0842 18.5409 10.7217C18.5596 10.5533 18.5083 10.4864 18.3109 10.4864C15.4271 10.4908 12.5434 10.4908 9.65966 10.4864C9.46847 10.4864 9.42342 10.5538 9.44451 10.7086C9.47949 10.9613 9.5092 11.2149 9.53555 11.469C9.61174 12.2014 9.6841 12.9339 9.76029 13.6663C9.84511 14.4858 9.92801 15.3052 10.0195 16.1238C10.1077 16.9131 10.1877 17.7041 10.3061 18.4899C10.4072 19.1607 11.1787 19.7886 11.9161 19.811C12.6023 19.8319 13.2895 19.8153 13.9762 19.8153H13.9771ZM16.4191 9.31321C16.2446 8.73163 15.5848 8.2187 14.9043 8.18109C14.3011 8.14786 13.6925 8.14874 13.0892 8.18109C12.4068 8.21783 11.7408 8.73644 11.5793 9.31321H16.4195H16.4191Z" fill="#F00C23"/>
              <path d="M11 17.2143C11.0955 17.0544 11.1752 16.8486 11.3145 16.7007C11.784 16.2001 12.2693 15.7137 12.7612 15.2358C12.9393 15.0628 12.9331 14.9478 12.7573 14.7771C12.2648 14.2992 11.7863 13.8071 11.3061 13.3167C10.9674 12.9709 10.9523 12.5287 11.2606 12.2107C11.5481 11.9136 12.0025 11.9306 12.335 12.2623C12.8342 12.76 13.3313 13.2606 13.8199 13.7686C13.9642 13.9188 14.0659 13.9211 14.2113 13.7708C14.7145 13.2504 15.2256 12.7373 15.7383 12.2265C15.9742 11.9913 16.3202 11.9369 16.5909 12.0746C16.8588 12.2112 17.0514 12.5276 16.9806 12.8229C16.9346 13.0134 16.8228 13.209 16.6886 13.3524C16.2264 13.8468 15.7462 14.3247 15.2627 14.7981C15.106 14.9517 15.1043 15.0606 15.2604 15.2148C15.7687 15.7165 16.2736 16.2228 16.7672 16.7392C17.1272 17.1157 17.0598 17.6264 16.633 17.9003C16.3578 18.0766 15.9709 18.0171 15.6889 17.7381C15.2014 17.2551 14.7134 16.7721 14.2416 16.2732C14.0664 16.0878 13.9608 16.1031 13.7924 16.2794C13.3122 16.7829 12.8213 17.2767 12.322 17.7608C11.948 18.1236 11.3724 18.0613 11.1415 17.6435C11.0803 17.5323 11.0595 17.3985 11 17.2137V17.2143Z" fill="#F00C23"/>
            </svg>
            <Text
              fontFamily="Urbanist, sans-serif"
              fontWeight="700"
              fontSize="16px"
              lineHeight="1em"
              color="#F00C23"
            >
              Cancel event
            </Text>
          </HStack>
        </Box>
      )}

      {/* Save Button */}
      <Box
        position="absolute"
        bottom="30px"
        left="22px"
        right="22px"
        zIndex={2}
      >
        <Button
          className='button-big'
          onClick={handleSave}
          isLoading={isSaving}
          loadingText="Saving..."
          isDisabled={isSaving || !isFormValid}
        >
          Save
        </Button>
      </Box>

      {/* Scroll to Top Button */}
      {showScrollToTop && !showCancelDialog && (
        <Box
          position="absolute"
          bottom="120px"
          right="22px"
          zIndex={1000}
          cursor="pointer"
          onClick={() => {
            console.log('Button clicked!'); // Debug log
            handleScrollToTop();
          }}
          _hover={{ transform: 'scale(1.1)' }}
          transition="transform 0.2s ease"
        >
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="18" cy="18" r="18" transform="rotate(90 18 18)" fill="#C6D959"/>
            <path d="M17.25 25C17.25 25.4142 17.5858 25.75 18 25.75C18.4142 25.75 18.75 25.4142 18.75 25L18 25L17.25 25ZM18.5303 11.4697C18.2374 11.1768 17.7626 11.1768 17.4697 11.4697L12.6967 16.2426C12.4038 16.5355 12.4038 17.0104 12.6967 17.3033C12.9896 17.5962 13.4645 17.5962 13.7574 17.3033L18 13.0607L22.2426 17.3033C22.5355 17.5962 23.0104 17.5962 23.3033 17.3033C23.5962 17.0104 23.5962 16.5355 23.3033 16.2426L18.5303 11.4697ZM18 25L18.75 25L18.75 12L18 12L17.25 12L17.25 25L18 25Z" fill="#120F1B"/>
          </svg>
        </Box>
      )}

      {/* Cancel Event Dialog */}
      {showCancelDialog && (
        <>
          {/* Modal Backdrop */}
          <Box className="modal-backdrop" zIndex={11} onClick={handleCancelEventClose} />

          {/* Dialog */}
          <Box
            className="bottom-sheet-dialog"
            //height="397px"
            zIndex={12}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Background Image */}
            <Box
              className="bottom-sheet-bg"
              style={{ backgroundImage: "url('/assets/images/confirmation-sheet-bg.svg')" }}
            />

            {/* Bottom Sheet Indicator */}
            <Box
              className="bottom-sheet-indicator"
              style={{ backgroundImage: "url('/assets/images/bottom-sheet-indicator.svg')" }}
            />

            {/* Content */}
            <VStack
              position="relative"
              zIndex={1}
              spacing="0"
              alignItems="flex-start"
              paddingTop="45px"
              paddingX="21px"
              height="100%"
            >
              {/* Title */}
              <Text
                className="static-text-big-white"
                marginBottom="20px"
              >
                Please indicate the reason for canceling the event
              </Text>

              {/* Text Area */}
              <Box
                className="event-details-long-text-box"
                backgroundColor="#120F1B"
              >
                <Textarea
                  className="textarea-business-event-creation"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Enter your reason..."
                  minHeight="128px"
                  padding="12px"
                />
              </Box>

              {/* Helper Text */}
              <Text
                className='static-text'
                marginBottom="30px"
                paddingLeft="12px"
              >
                minimum 80 characters...
              </Text>
            <Box
             className="button-big-dialog-container"
            >
              <Button
                className="button-big"
                onClick={handleSendCancelReason}
              >
                Cancel event
              </Button>
              </Box>
            </VStack>
            <Box
              className="bottom-sheet-footer"
              style={{ backgroundImage: "url('/assets/images/home-indicator-confirmation.svg')" }}
            />
          </Box>
        </>
      )}


    </Box>
  );
};

export default BusinessEventCreationPage;
